import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Pagination } from '@common/types/pagination.types';
import { formatDate } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class oDataService {
    constructor(
        private datepipe: DatePipe,
    ) { }

    formatDateStr(paramDate: Date) {
        return formatDate(paramDate, 'yyyy-MM-dd', 'en-US');

    }
    setDateRangeFilter(key: string, value: any) {
        let filterString = "";
        let operator = "";
        let actualKey = "";
        let valueStr = "";

        if (key.endsWith("__From")) {
            actualKey = key.replace("__From", "");
            operator = "ge";
            valueStr = this.formatDateStr(value);
        }
        else {
            actualKey = key.replace("__To", "");
            operator = "lt";
            valueStr = this.formatDateStr(value);
            let date = new Date(valueStr);
            date.setDate(date.getDate() + 1);
            valueStr = this.formatDateStr(date);

        }
        filterString = `(${actualKey} ${operator} ${valueStr})`;
        return filterString;
    }
    setFilters(filters: {}, pagination: Pagination, withJoin?: string): string {
        let url = '?';
        let items = Object.entries(this.removeDefaultValues(filters));
        if (items.length > 0) {
            url = url.concat("$filter=");
            items.forEach(([key, value], index) => {
                if (key.includes('.'))
                    key = key.replace('.', '/')

                // Treat date range filters (__From / __To) specially regardless of JS type (Date or ISO string)
                if (key.endsWith("__From") || key.endsWith("__To")) {
                    let dateVal: any = value;
                    if (typeof value === 'string') {
                        const parsed = new Date(value);
                        if (!isNaN(parsed.getTime())) {
                            dateVal = parsed;
                        }
                    }

                    if (dateVal && (dateVal instanceof Date || typeof dateVal === 'object')) {
                        url = url.concat(this.setDateRangeFilter(key, dateVal), this.appendAnd(items.length - 1, index));
                        return; // continue to next item
                    }
                }

                switch (typeof value) {
                    case 'object':
                        // non-date objects fall through (handled elsewhere) - ignore here
                        break;
                    case 'string':
                        if (key === 'locationCode') {
                            url = url.concat("(", key, " eq '", value, "')", this.appendAnd(items.length - 1, index));
                        } else {
                            url = url.concat("(contains(", key, ",'", value, "') eq true)", this.appendAnd(items.length - 1, index));
                        }

                        // url = url.concat("(contains(", key, ",'", value, "') eq true)", this.appendAnd(items.length - 1, index));
                        break;
                    case 'number':
                        url = url.concat("(", key, " eq " + value, ")", this.appendAnd(items.length - 1, index));
                        break;
                    case 'boolean':
                        url = url.concat(key, " eq " + value, this.appendAnd(items.length - 1, index));
                        break;
                    default:
                        break;
                };
            });
        }
        if (withJoin) {
            url = url.concat("&$expand=", withJoin);
        }

        if (pagination) {
            url = url.concat("&$top=" + pagination.size, "&$skip=" + pagination.size * pagination.page);

            if (pagination.sort) {
                var multileSort = pagination.sort.split(',');
                if (multileSort.length > 1) {
                    let orderByStr = "&$orderby=";
                    multileSort.forEach((sortItem, index) => {
                        orderByStr = orderByStr.concat(sortItem.replace('.', '/'), " ", pagination.order);
                        if (index < multileSort.length - 1)
                            orderByStr = orderByStr.concat(",");
                    });
                    url = url.concat(orderByStr);
                }
                else {
                    url = url.concat("&$orderby=", pagination.sort.replace('.', '/'), " ", pagination.order);
                }
            }

            url = url.concat("&$count=true");
        }
        return url;
    }
    setMultipleValueFilters(filters: {}, pagination: Pagination, withJoin?: string): string {
        let url = '?';
        const items = Object.entries(this.removeDefaultValues(filters));
        const filterClauses: string[] = [];

        for (const [keyRaw, value] of items) {
            // OData path transform (a.b -> a/b)
            let key = keyRaw.includes('.') ? keyRaw.replace('.', '/') : keyRaw;

            if (value === undefined || value === null || value === '') continue;

            if (Array.isArray(value) && value.length > 0) {
                if (key.endsWith('__IN__CSV')) {
                    // e.g. key = CollectionIdsCsv__IN_CSV  OR  collectionIdsCsv__IN_CSV
                    // Build: contains(concat(',', CollectionIdsCsv, ','), ',<v>,') [or/and ...]
                    const baseKey = key.replace('__IN__CSV', '');

                    // ANY semantics (matches if PO has any of the selected collections)
                    const anyClauses = value
                        .filter(v => v !== null && v !== undefined && `${v}`.trim().length > 0)
                        .map(v => `contains( ${baseKey},',${v},')`);
                    if (anyClauses.length > 0) {
                        filterClauses.push(`(${anyClauses.join(' or ')})`);
                    }

                    // If you ever need ALL semantics instead, use:
                    // const allClauses = value.map(v => `contains(concat(',', ${baseKey}, ','), ',${v},')`);
                    // filterClauses.push(`(${allClauses.join(' and ')})`);

                } else if (key.endsWith('__IN')) {
                    // Existing __IN (collection-valued nav): baseKey/any(c: c eq v1 or c eq v2 ...)
                    const baseKey = key.replace('__IN', '');
                    const clause = value.map(v => `c eq ${typeof v === 'string' ? `'${v}'` : v}`).join(' or ');
                    filterClauses.push(`${baseKey}/any(c: ${clause})`);

                } else {
                    // Generic OR over an array of scalars
                    const clause = value
                        .map(v => `(${key} eq ${typeof v === 'string' ? `'${v}'` : v})`)
                        .join(' or ');
                    filterClauses.push(`(${clause})`);
                }

            } else if ((key.endsWith('__From') || key.endsWith('__To'))) {
                // Accept either Date objects or ISO date strings for range filtering.
                let dateVal: any = value;
                if (typeof value === 'string') {
                    // Try to parse ISO date strings (and tolerate timezone offsets)
                    const parsed = new Date(value);
                    if (!isNaN(parsed.getTime())) {
                        dateVal = parsed;
                    }
                }

                if (dateVal && (dateVal instanceof Date || typeof dateVal === 'object')) {
                    const clause = this.setDateRangeFilter(key, dateVal);
                    if (clause) filterClauses.push(clause);
                }

            } else if (typeof value === 'string') {
                filterClauses.push(`(contains(${key},'${value.replace(/'/g, "''")}') eq true)`);

            } else if (typeof value === 'number') {
                filterClauses.push(`(${key} eq ${value})`);

            } else if (typeof value === 'boolean') {
                filterClauses.push(`${key} eq ${value}`);
            }
        }

        if (filterClauses.length > 0) {
            // URL-encode the $filter to avoid breaking on spaces/quotes
            url += `$filter=${encodeURIComponent(filterClauses.join(' and '))}`;
        }

        if (withJoin) {
            url += `&$expand=${withJoin}`;
        }

        if (pagination) {
            url = url.concat("&$top=" + pagination.size, "&$skip=" + (pagination.size * pagination.page));

            if (pagination.sort) {
                const multileSort = pagination.sort.split(',');
                if (multileSort.length > 1) {
                    let orderByStr = "&$orderby=";
                    multileSort.forEach((sortItem, index) => {
                        orderByStr = orderByStr.concat(sortItem.replace('.', '/'), " ", pagination.order);
                        if (index < multileSort.length - 1) orderByStr = orderByStr.concat(",");
                    });
                    url = url.concat(orderByStr);
                } else {
                    url = url.concat("&$orderby=", pagination.sort.replace('.', '/'), " ", pagination.order);
                }
            }

            url = url.concat("&$count=true");
        }

        return url;
    }

    appendAnd(itemsLength, index): string {
        if (index === itemsLength)
            return '';
        else
            return ' and ';
    }

    removeDefaultValues(filters) {
        if (!filters) {
            return {};
        }
        return Object.entries(filters)
            .filter(([key, value]) =>
                value !== '' && value !== null)
            .reduce((acc, [key, value]) => {
                acc[key] = filters[key];
                return acc;
            }, {});
    }
}